import { useEffect, useState, useCallback } from 'react';
import {
  loadUserDrafts, migrateLegacyIfAny, draftSummary,
  DRAFTS_KEY, LEGACY_KEY, DRAFT_CHANGE_EVENT,
} from './inspection-draft';
import { hydrateFromServer } from './inspection-draft-sync';
import { sigSessionsAPI } from './api';
import { useAuth } from './auth-context';

// Reactive hook returning the CURRENT USER's in-progress inspection drafts.
// Returns: { drafts: [...], summaries: [...], any: bool }
// Hydrates from the server on mount + every 60s so drafts authored on
// another device surface here automatically.
//
// Block 2 behavior:
//   - Filters out SIG drafts (`_sig_recovery_only=true`) — those are
//     crash-recovery only and never user-facing.
//   - Synthesizes a "draft" record from the user's active sig_session
//     (if ASUP) so the Resume Inspection card stack shows the live SIG
//     alongside individual drafts.
export function useInspectionDraftPresence() {
  const { user } = useAuth();
  const userId = user?._id || user?.id || null;
  const isAsup = user?.role === 'approving_supervisor';

  const [sigDraft, setSigDraft] = useState(null);  // synthesized from sig_sessions

  const read = useCallback(() => {
    if (!userId) return { drafts: [], summaries: [], any: false };
    migrateLegacyIfAny(userId);
    const allDrafts = loadUserDrafts(userId);
    // Block 2: hide SIG recovery-only drafts from the Resume card stack.
    const drafts = allDrafts.filter((d) => !d?._sig_recovery_only);
    // Append the live sig-session synthesized draft (if any) at the top.
    const combined = sigDraft ? [sigDraft, ...drafts] : drafts;
    return {
      drafts: combined,
      summaries: combined.map(draftSummary),
      any: combined.length > 0,
    };
  }, [userId, sigDraft]);

  const [state, setState] = useState(() => read());

  useEffect(() => {
    setState(read());
  }, [read]);

  // Synthesize a Resume-card record from the active sig_session (ASUPs only).
  useEffect(() => {
    if (!userId || !isAsup) { setSigDraft(null); return undefined; }
    let cancelled = false;
    const pullSig = async () => {
      try {
        const { data } = await sigSessionsAPI.byUser(userId);
        if (cancelled) return;
        if (!data) { setSigDraft(null); return; }
        // Build a draft-card-compatible shape.
        const synth = {
          _key: `sig:${data._id}`,
          _sig_session_id: data._id,
          _is_sig_session: true,
          inspectionType: 'sig',
          selectedStation: data.station_id,
          progress: {
            station_name: data.station_name,
            inspected: data.item_count || 0,
            // total filled in by SigResumeBanner via coverage; default to a soft estimate
            total: data.item_count || 0,
          },
          saved_at: data.updated_at ? new Date(data.updated_at).getTime() : Date.now(),
        };
        setSigDraft(synth);
      } catch { /* ignore */ }
    };
    pullSig();
    const t = setInterval(pullSig, 30_000);
    const onChange = () => pullSig();
    window.addEventListener('sig:session-changed', onChange);
    return () => { cancelled = true; clearInterval(t); window.removeEventListener('sig:session-changed', onChange); };
  }, [userId, isAsup]);

  // Pull server drafts into localStorage on mount + every 60s. The save
  // helper fires DRAFT_CHANGE_EVENT so the refresh path below will pick
  // up any newly-pulled drafts automatically.
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        await hydrateFromServer(userId);
      } catch (_) { /* noop */ }
      if (!cancelled) setState(read());
    };
    pull();
    const hydrateTick = setInterval(pull, 60_000);
    return () => {
      cancelled = true;
      clearInterval(hydrateTick);
    };
  }, [userId, read]);

  useEffect(() => {
    const refresh = () => setState(read());
    const onStorage = (e) => {
      if (!e.key || e.key === DRAFTS_KEY || e.key === LEGACY_KEY) refresh();
    };
    window.addEventListener(DRAFT_CHANGE_EVENT, refresh);
    window.addEventListener('storage', onStorage);
    const tick = setInterval(refresh, 60000);
    return () => {
      window.removeEventListener(DRAFT_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', onStorage);
      clearInterval(tick);
    };
  }, [read]);

  return state;
}
